import { FormattedMessage } from 'react-intl';
import React, { useEffect, useState } from 'react';
import { getParsedParams } from '../../../utils/consts/params';
import {
  isNotificationKey,
  notificationData,
  type NotificationKey,
  notificationKeys,
} from '../../../utils/consts/profileNotifications';
import checkImage from '../../../../assets/icons/check-green.svg';
import errorImage from '../../../../assets/icons/alert-exclamation.svg';
import styles from './FloatingNotification.scss';

const NOTIFICATION_DURATION_MS = 4000;

const STORED_NOTIFICATION_KEY = 'storedNotification';

const storeNotification = (key: NotificationKey) => {
  localStorage.setItem(STORED_NOTIFICATION_KEY, key);
};

interface NotificationContextState {
  currentNotificationKey: NotificationKey | null;
  addNotification: (key: NotificationKey) => void;
  removeCurrentNotification: () => void;
  /** Store notification to be shown on next app render, i.e. after page reload */
  storeNotification: (key: NotificationKey) => void;
}

const NotificationContext = React.createContext<NotificationContextState>({
  currentNotificationKey: null,
  addNotification: () => {},
  removeCurrentNotification: () => {},
  storeNotification: () => {},
});

const NotificationContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [notificationsQueue, setNotificationsQueue] = useState<NotificationKey[]>([]);

  useEffect(() => {
    const storedNotification = localStorage.getItem(STORED_NOTIFICATION_KEY);

    if (storedNotification && isNotificationKey(storedNotification)) {
      setNotificationsQueue((prev) => [...prev, storedNotification]);
      localStorage.removeItem(STORED_NOTIFICATION_KEY);
    }
  }, []);

  useEffect(() => {
    const frontNotification = notificationsQueue[0];

    if (notificationsQueue.length === 0) {
      return;
    }

    const removeFront = (q: NotificationKey[]) => q.filter((key) => key !== frontNotification);

    const timer = setTimeout(() => {
      setNotificationsQueue(removeFront);
    }, NOTIFICATION_DURATION_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [notificationsQueue]);

  return (
    <NotificationContext.Provider
      value={{
        currentNotificationKey: notificationsQueue[0] ?? null,
        addNotification: (key: NotificationKey) => {
          setNotificationsQueue((prev) => [...prev, key]);
        },
        removeCurrentNotification: () => {
          setNotificationsQueue((prev) => prev.slice(1));
        },
        storeNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

const useNotificationContext = () => React.useContext(NotificationContext);

const FloatingNotification = () => {
  const { currentNotificationKey, removeCurrentNotification, addNotification } = useNotificationContext();

  useEffect(() => {
    const { PROFILE_NOTIFICATION } = getParsedParams();

    if (PROFILE_NOTIFICATION === notificationKeys.EMAIL_CHANGED_NOTIFICATION) {
      addNotification(notificationKeys.EMAIL_CHANGED_NOTIFICATION);
      window.history.replaceState({}, document.title, '/profile');
    }
  }, []);

  if (!currentNotificationKey) {
    return null;
  }

  return (
    <aside className={styles.container}>
      <button
        type="button"
        className={styles.notification}
        onClick={() => {
          removeCurrentNotification();
        }}
      >
        {notificationData[currentNotificationKey].kind === 'success' && (
          <img src={checkImage} alt="" width={24} height={24} />
        )}
        {notificationData[currentNotificationKey].kind === 'error' && (
          <img src={errorImage} alt="" width={24} height={24} />
        )}

        <span className={styles.text}>
          <FormattedMessage
            id={notificationData[currentNotificationKey].text.id}
            defaultMessage={notificationData[currentNotificationKey].text.defaultMessage}
          />
        </span>
      </button>
    </aside>
  );
};

export { FloatingNotification, NotificationContextProvider, useNotificationContext };
